import React from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography, alpha } from "@mui/material";
import { FaArrowsRotate } from "react-icons/fa6";
import dayjs, { Dayjs } from "dayjs";

/**
 * Reload the current list without changing it.
 *
 * Admin screens are watched while something is happening elsewhere -- an order
 * being paid, a refund going through, a supplier answering -- and the only way
 * to see the new state was to reload the browser, which throws away the
 * filters, the page and the sort that were set up to watch it in the first
 * place.
 *
 * This re-runs the same request with everything left exactly as it is.
 *
 * Shared rather than written per screen so that every list refreshes the same
 * way and shows the same "last updated" wording.
 */

type Props = {
  /** Re-run the list request. Usually bumps a key the fetch effect depends on. */
  onRefresh: () => void;
  /** True only while a refresh this button started is in flight. */
  refreshing?: boolean;
  /** When the list last came back, so the screen can be trusted at a glance. */
  lastUpdated?: Dayjs | null;
  /** Hide the timestamp where there is no room for it. */
  showTimestamp?: boolean;
  size?: number;
};

const RefreshButton: React.FC<Props> = ({
  onRefresh,
  refreshing = false,
  lastUpdated,
  showTimestamp = true,
  size = 15,
}) => {
  /*
   * A clock time, not "3 minutes ago".
   *
   * Relative wording goes stale the moment it is rendered -- it would still
   * read "just now" ten minutes later unless a timer redrew it. An absolute
   * time is right without anything having to keep it right.
   */
  const stamp = lastUpdated ? dayjs(lastUpdated).format("HH:mm:ss") : null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {showTimestamp && stamp && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
        >
          Updated {stamp}
        </Typography>
      )}
      <Tooltip title={refreshing ? "Refreshing..." : "Refresh this list"}>
        {/*
          A disabled button gives no tooltip and swallows the pointer events
          Tooltip listens for, so the span keeps it explainable while it spins.
        */}
        <span>
          <IconButton
            onClick={onRefresh}
            disabled={refreshing}
            size="small"
            aria-label="Refresh this list"
            /*
             * `primary.lighter` is not a real token -- nothing in this app
             * defines one, so it resolves to nothing and the button loses its
             * background. Other screens use `success.lighter` the same way and
             * have the same missing background. These are all standard MUI
             * tokens, so they work on whichever palette is active.
             */
            sx={(theme) => ({
              color: "primary.main",
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              height: 34,
              width: 34,
              "&:hover": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              },
              // Turn while loading, so it is obvious the click was taken.
              "& svg": refreshing
                ? { animation: "itopup-spin 0.9s linear infinite" }
                : {},
              "@keyframes itopup-spin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
              "@media (prefers-reduced-motion: reduce)": {
                "& svg": { animation: "none" },
              },
            })}
          >
            {refreshing ? <CircularProgress size={size} color="inherit" /> : <FaArrowsRotate size={size} />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default RefreshButton;
